import { useCallback } from 'react';
import { useRestrictFolder } from '@frameio/assets/operations';
import type { PlanFeatureInput } from '@frameio/graphql-client/generated';
import { useFolderPermissions } from '@frameio/permissions';
import { FeatureGate } from '@frameio/plan-management/feature-gate/components';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { useFeatureGateDisableContext } from '@frameio/plan-management/feature-gate/hooks/useFeatureGateDisableContext';
import { RestrictedAccessToggle } from '@frameio/shared/components';
import { useDebounceCallback } from '@frameio/vapor';
import { useIsRestrictedFolder } from '../../hooks/useIsRestrictedFolder';
import TEXT from './generated/TEXT/FolderAccessToggle';

TEXT.DEFINE({
  /** Title for the folder access toggle */
  title: 'Make Restricted',
  /** Description for the folder access toggle */
  description:
    'Only people directly invited to this folder will have access, plus admins.',
  /** Error message for the folder access toggle */
  error: 'Something went wrong',
});

const DEBOUNCE = 100;
const RESTRICTED_FOLDERS: PlanFeatureInput = 'RESTRICTED_FOLDERS';

type FolderAccessToggleProps = {
  folderId: string;
};

export const FolderAccessToggle = ({ folderId }: FolderAccessToggleProps) => {
  return (
    <FeatureGate
      feature={RESTRICTED_FOLDERS}
      placement="top"
      shouldShowTooltip={true}
    >
      <ToggleContainer folderId={folderId} />
    </FeatureGate>
  );
};

const ToggleContainer = ({ folderId }: FolderAccessToggleProps) => {
  const isRestrictedFolder = useIsRestrictedFolder(folderId);

  const { restrictFolder, restrictWarningDialog } = useRestrictFolder();

  const featureGatePlanSuggestion =
    useFeatureGatePlanSuggestion(RESTRICTED_FOLDERS);
  const shouldUseFeatureGateButton =
    !featureGatePlanSuggestion?.includedInCurrentPlan;

  const featureGateDisableContext = useFeatureGateDisableContext();
  const isFeatureGateActive =
    featureGateDisableContext?.isUpgradeModalButtonActive ?? false;

  const canManageFolderAccess = useFolderPermissions({
    check: 'canEditUserPermissions',
    folderId,
  });

  const handleSetFolderRestricted = useCallback(
    (restrictedValue?: boolean) => {
      if (restrictedValue === undefined) return;
      restrictFolder({
        folderId,
        restricted: restrictedValue,
      });
    },
    [folderId, restrictFolder]
  );

  const [debouncedOnChange] = useDebounceCallback(
    handleSetFolderRestricted,
    DEBOUNCE,
    {
      leading: true,
    }
  );

  const handleDebouncedOnChange = useCallback(() => {
    debouncedOnChange(!isRestrictedFolder);
  }, [debouncedOnChange, isRestrictedFolder]);

  // useFeatureGatePlanSuggestion returns undefined until the cache is populated
  if (featureGatePlanSuggestion === undefined) return null;

  return (
    <>
      <RestrictedAccessToggle
        canManageAccess={canManageFolderAccess}
        description={TEXT.description}
        isFeatureGateActive={isFeatureGateActive}
        isFeatureGated={shouldUseFeatureGateButton}
        isRestricted={isRestrictedFolder}
        onChange={handleDebouncedOnChange}
        title={TEXT.title}
      />

      {restrictWarningDialog}
    </>
  );
};
